// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { findLastIndex } from 'lodash-es';
import { Colours as SectorColours } from '../../../../base-styles/colours';
import { Colours } from '../../../../design-system/colours';
import { Dict } from '../../../../model/dict';
import { EmissionBreakDownSeries } from '../../../../modules/diagram/components/emission-scenario-diagram/emission-break-down-series';
import { EmissionScenarioDiagramSeries } from '../../../../modules/diagram/components/emission-scenario-diagram/emission-scenario-diagram-series';
import { LineSeriesStyle } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/line/line.series-style';
import { Series } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series';
import { SeriesData } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series-data';
import { SeriesType } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series-type';
import { StyledSeries } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/styled-series';
import { RGBAColour } from '../../../../modules/diagram/renderers/styles/rgba-colour';
import { EmissionNode } from '../../../../public/dashboard/components/dashboard-emissions/emission-node';
import { TransitionScenarioDiagramData } from '../../../../public/dashboard/components/dashboard-transitions/transition-scenario-diagram-data';
import { STATIC_DASHBOARD_IDS } from '../../../../public/dashboard/shared/dashboard.constants';
import { DashboardColours } from '../../../../public/dashboard/shared/model/dashboard-colours';
import { DashboardEmissionSeries } from '../../../../public/dashboard/shared/model/dashboard-emission-series';
import { DashboardNodeV4 } from '../../../../public/dashboard/shared/model/dashboard-node-v4/dashboard-node-v4';
import { EmissionScenarioItem } from '../emission-data/emission-scenario-item';
import { FutureEmissionsItem } from '../emission-data/future-emissions-item';
import { MainTransitionPathwayItem } from '../emission-data/main-transition-pathway-item';
import { MainTransitionScenarioItem } from '../emission-data/main-transition-scenario-item';
import { TransitionPathwayItem } from '../emission-data/transition-pathway-item';
import { TransitionScenarioItem } from '../emission-data/transition-scenario-item';
import { TransitionItem } from '../transition-data/transition-item';
import { limitAbatement } from './limit-abatement';

@Injectable({
  providedIn: 'root',
})
export class DiagramSeriesService {
  /**
   * @deprecated used for v4.1, replaced by createEmissionScenarioDiagramSeries for 5
   * @param futureEmissionsData
   * @returns
   */
  createEmissionDataSeries(futureEmissionsData: { [year: number]: FutureEmissionsItem }): DashboardEmissionSeries[] {
    const referenceEmissionsData: SeriesData[] = [];
    const goalEmissionsData: SeriesData[] = [];
    const historicalEmissionsData: SeriesData[] = [];
    Object.keys(futureEmissionsData).forEach((year) => {
      const item: FutureEmissionsItem = futureEmissionsData[year];
      referenceEmissionsData.push({ label: year.toString(), value: this._roundValue(item.referenceEmissions) });
      goalEmissionsData.push({ label: year.toString(), value: this._roundValue(item.goalEmissions) });
      historicalEmissionsData.push({ label: year.toString(), value: this._roundValue(item.historicalEmissions) });
    });
    const referenceEmissions: DashboardEmissionSeries = {
      data: referenceEmissionsData,
      label: STATIC_DASHBOARD_IDS.REFERENCE_ID,
      id: STATIC_DASHBOARD_IDS.REFERENCE_ID,
      style: {
        type: SeriesType.Line,
        strokeStyle: {
          colour: Colours.red400,
          width: 3,
          strokeType: {
            dash: {
              dash: 8,
              gap: 2,
            },
          },
        },
      },
    };
    const goalEmissions: DashboardEmissionSeries = {
      data: goalEmissionsData,
      label: STATIC_DASHBOARD_IDS.GOAL_ID,
      id: STATIC_DASHBOARD_IDS.GOAL_ID,
      style: {
        type: SeriesType.Line,
        strokeStyle: {
          colour: Colours.green300,
          width: 3,
          strokeType: {
            dash: {
              dash: 4,
              gap: 4,
            },
          },
        },
      },
    };
    const historicalEmissions: DashboardEmissionSeries = {
      data: historicalEmissionsData,
      label: STATIC_DASHBOARD_IDS.HISTORICAL_EMISSIONS_ID,
      id: STATIC_DASHBOARD_IDS.HISTORICAL_EMISSIONS_ID,
      style: {
        type: SeriesType.Line,
        strokeStyle: {
          colour: RGBAColour.fromHex('#ffffff'),
          width: 3,
        },
        dotStyle: {
          colour: RGBAColour.fromHex('#ffffff'),
          diameter: 8,
        },
      },
    };
    return [goalEmissions, referenceEmissions, historicalEmissions];
  }

  createEmissionScenarioDiagramSeries(
    rootId: string,
    nodes: Dict<EmissionNode>,
    sinkData: { title: string; colours: DashboardColours },
    emissionScenarioData: Dict<{ [year: string]: EmissionScenarioItem }>,
    showGoalDots: boolean
  ): Dict<EmissionScenarioDiagramSeries> {
    return Object.keys(emissionScenarioData).reduce((dict: Dict<EmissionScenarioDiagramSeries>, scenarioId) => {
      const referenceEmissionsData: SeriesData[] = [];
      const goalEmissionsData: SeriesData[] = [];
      const reportedEmissionsData: SeriesData[] = [];
      const scenarioEmissionsData: SeriesData[] = [];
      const areaSeries: Dict<SeriesData[]> = {};
      const data = emissionScenarioData[scenarioId];
      Object.keys(data).forEach((year) => {
        const item: EmissionScenarioItem = data[year];
        referenceEmissionsData.push({ label: year.toString(), value: this._roundValue(item.referenceEmissions) });
        goalEmissionsData.push({ label: year.toString(), value: this._roundValue(item.goalEmissions) });
        reportedEmissionsData.push({ label: year.toString(), value: this._roundValue(item.reportedEmissions) });
        scenarioEmissionsData.push({ label: year.toString(), value: this._roundValue(item.scenarioEmissions) });
        Object.entries(item.childScenarioEmissions).forEach(([nodeId, value]) => {
          if (!areaSeries[nodeId]) {
            areaSeries[nodeId] = [];
          }
          areaSeries[nodeId].push({ label: year.toString(), value: this._roundValue(value) });
        });
      });
      const referenceEmissionSeries: DashboardEmissionSeries = {
        data: referenceEmissionsData,
        label: STATIC_DASHBOARD_IDS.REFERENCE_ID,
        id: STATIC_DASHBOARD_IDS.REFERENCE_ID,
        style: {
          type: SeriesType.Line,
          strokeStyle: {
            colour: Colours.red400,
            width: 3,
            strokeType: {
              dash: {
                dash: 8,
                gap: 2,
              },
            },
          },
        },
      };
      const goalStyle: LineSeriesStyle = {
        type: SeriesType.Line,
        strokeStyle: {
          colour: Colours.green300,
          width: 3,
          strokeType: {
            dash: {
              dash: 4,
              gap: 4,
            },
          },
        },
      };

      if (showGoalDots) {
        goalStyle.dotStyle = {
          colour: Colours.green300,
          diameter: 8,
        };
      }
      const goalEmissionSeries: DashboardEmissionSeries = {
        data: goalEmissionsData,
        label: STATIC_DASHBOARD_IDS.GOAL_ID,
        id: STATIC_DASHBOARD_IDS.GOAL_ID,
        style: goalStyle,
      };
      const reportedEmissionSeries: DashboardEmissionSeries = {
        data: reportedEmissionsData,
        label: STATIC_DASHBOARD_IDS.HISTORICAL_EMISSIONS_ID,
        id: STATIC_DASHBOARD_IDS.HISTORICAL_EMISSIONS_ID,
        style: {
          type: SeriesType.Line,
          strokeStyle: {
            colour: RGBAColour.fromHex('#ffffff'),
            width: 3,
          },
          dotStyle: {
            colour: RGBAColour.fromHex('#ffffff'),
            diameter: 8,
          },
        },
      };
      const scenarioEmissionSeries: DashboardEmissionSeries = {
        data: scenarioEmissionsData,
        label: STATIC_DASHBOARD_IDS.SCENARIO_EMISSIONS_ID,
        id: STATIC_DASHBOARD_IDS.SCENARIO_EMISSIONS_ID,
        style: {
          type: SeriesType.Line,
          strokeStyle: {
            colour: RGBAColour.fromHex('#ffffff'),
            strokeType: {
              dash: {
                dash: 4,
                gap: 4,
              },
            },
            width: 3,
          },
        },
      };
      const emissionAreaSeries: EmissionBreakDownSeries[] = Object.entries(areaSeries).map(([id, seriesData]) => {
        const node = id === STATIC_DASHBOARD_IDS.EMISSION_SINK_ID ? sinkData : nodes[id];
        const defaultColour = Colours.grey400;
        return {
          data: seriesData,
          label: node?.title ?? '',
          leafNode: scenarioId === id,
          id: id,
          yearHighlightColour: RGBAColour.fromHex(node?.colours.main ?? defaultColour),
          yearHighlightHoverColour: RGBAColour.fromHex(node?.colours.light ?? defaultColour),
          style: {
            type: SeriesType.Area,
            fillColour: RGBAColour.fromHex(node?.colours.dark ?? defaultColour),
            separator: {
              colour: RGBAColour.fromHex(Colours.primaryDashboard700 ?? defaultColour),
              width: 2,
            },
          },
          highlightStyle: {
            type: SeriesType.Area,
            fillColour: RGBAColour.fromHex(node?.colours.darkHover ?? node?.colours.light ?? defaultColour),
            separator: {
              colour: RGBAColour.fromHex(Colours.primaryDashboard700),
              width: 2,
            },
          },
        };
      });
      emissionAreaSeries.reverse(); //reversing since we want the first node to be at the top of the area chart. Series in the area chart is added bottom up.
      dict[scenarioId] = {
        nodeId: scenarioId,
        emissionLineSeries: [
          scenarioId === rootId ? goalEmissionSeries : scenarioEmissionSeries,
          referenceEmissionSeries,
          reportedEmissionSeries,
        ],
        emissionAreaSeries,
        scenarioEmissionSeries,
      };
      return dict;
    }, {});
  }
  /**
   * @deprecated used for v4.1, replaced by createTransitionScenarioData for 5
   */
  createTransitionCarbonAbatementDataSeries(
    nodes: Dict<DashboardNodeV4>,
    transitionData: Dict<{ [year: number]: TransitionItem }>
  ): Dict<StyledSeries[]> {
    const styledSeries: Dict<StyledSeries[]> = {};
    Object.values(nodes).forEach((node) => {
      const data = transitionData[node.id];
      if (data) {
        const carbonAbatementData: SeriesData[] = [];
        Object.keys(data).forEach((year) => {
          const entry = data[year];
          carbonAbatementData.push({ label: year, value: this._roundValue(entry.carbonAbatement) });
        });
        //Todo: exclude empty series? e.g series with only undefined values.
        const carbonAbatementSeries: Series = {
          data: carbonAbatementData,
          label: STATIC_DASHBOARD_IDS.CARBON_ABATEMENT_ID,
          id: STATIC_DASHBOARD_IDS.CARBON_ABATEMENT_ID,
        };
        styledSeries[node.id] = [this._styleAsAreaSeries(carbonAbatementSeries, node.colours.dark, node.colours.light)];
      }
    });
    return styledSeries;
  }

  /**
   * @deprecated used for v4.1, replaced by createTransitionScenarioData for 5
   */
  createTransitionPathwayDataSeries(
    boardId: string,
    nodes: Dict<DashboardNodeV4>,
    mainTransitionPathway: { [year: number]: MainTransitionPathwayItem },
    transitionPathwayData: Dict<{ [year: number]: TransitionPathwayItem }>
  ): Dict<StyledSeries[]> {
    const styledSeries: Dict<StyledSeries[]> = {};
    const styleEmissionSeries = (series: Series) => {
      return this._styleAsAreaSeries(series, Colours.primaryDashboard500, Colours.primaryDashboard300);
    };
    const styleOtherReductionSeries = (series: Series) => {
      return this._styleAsAreaSeries(series, Colours.primaryDashboard200, Colours.primaryDashboard100);
    };
    const styleGapSeries = (series: Series) => {
      return this._styleAsAreaSeries(series, Colours.primaryDashboard200, Colours.primaryDashboard100);
    };
    const styleEnergySeries = (series: Series) => {
      return this._styleAsAreaSeries(series, SectorColours.sectors.energy.dark, SectorColours.sectors.energy.light);
    };
    const styleAbatementSeries = (series: Series, node: DashboardNodeV4) => {
      return this._styleAsAreaSeries(series, node.colours.dark ?? '#aaaaaa', node.colours.light ?? '#ffffff');
    };

    const years = Object.keys(mainTransitionPathway);
    let mainEmissionData: number[] = [];
    const mainGapData: number[] = [];
    const mainAbatementData: Dict<number[]> = {};
    years.forEach((year) => {
      const item: MainTransitionPathwayItem = mainTransitionPathway[year];
      mainEmissionData.push(this._roundValue(item.remainingEmissions));
      mainGapData.push(this._roundValue(item.gap));
      Object.keys(item.abatement).forEach((key) => {
        const abatement = item.abatement[key];
        const abatementId = abatement.id;
        if (!mainAbatementData[abatementId]) {
          mainAbatementData[abatementId] = [];
        }
        mainAbatementData[abatementId].push(this._roundValue(abatement.value));
      });
    });
    let allAbatementData = Object.keys(mainAbatementData).map((abatementId) => {
      const abatementData = mainAbatementData[abatementId];
      return abatementData;
    });
    //reversing since we know this will be reversed for display.
    allAbatementData.reverse();
    allAbatementData = limitAbatement(mainEmissionData, allAbatementData);
    allAbatementData.reverse();
    Object.keys(mainAbatementData).forEach((abatementId, index) => {
      mainAbatementData[abatementId] = allAbatementData[index];
    });
    mainEmissionData = mainEmissionData.map((value) => Math.max(0, value));
    const mainEmissions: StyledSeries = styleEmissionSeries({
      data: this._valuesToSeriesData(years, mainEmissionData),
      label: STATIC_DASHBOARD_IDS.REMAINING_EMISSIONS_ID,
      id: STATIC_DASHBOARD_IDS.REMAINING_EMISSIONS_ID,
    });
    let mainSeries = [mainEmissions];
    const hasValidGapSeries = mainGapData.some((v) => v > 0);
    if (hasValidGapSeries) {
      const validGapData = mainGapData.map((value) => (value === undefined ? value : Math.max(0, value)));
      const mainGap: StyledSeries = styleGapSeries({
        data: this._valuesToSeriesData(years, validGapData),
        label: STATIC_DASHBOARD_IDS.MAIN_GAP_ID,
        id: STATIC_DASHBOARD_IDS.MAIN_GAP_ID,
      });
      mainSeries.push(mainGap);
    }
    const abatementSeries = Object.keys(mainAbatementData)
      .map((abatementId) => {
        const series: Series = {
          data: this._valuesToSeriesData(years, mainAbatementData[abatementId]),
          label: abatementId,
          id: abatementId,
        };
        if (abatementId === STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID) {
          return styleEnergySeries(series);
        }
        const node = nodes[abatementId];
        return styleAbatementSeries(series, node);
      })
      .reverse(); //reversing since we want the first node to be at the top of the area chart. Series in the area chart is added bottom up.
    mainSeries = mainSeries.concat(abatementSeries);
    styledSeries[boardId] = mainSeries;
    Object.values(nodes).forEach((node) => {
      const pathwayData = transitionPathwayData[node.id];
      let remainingSeriesData: number[] = [];
      let otherAreasData: number[] = [];
      let abatementData: number[] = [];
      years.forEach((year) => {
        const item: TransitionPathwayItem = pathwayData[year];
        remainingSeriesData.push(this._roundValue(item.remainingEmissions));
        otherAreasData.push(this._roundValue(item.otherAreas));
        abatementData.push(this._roundValue(item.abatement));
      });
      [otherAreasData, abatementData] = limitAbatement(remainingSeriesData, [otherAreasData, abatementData]);
      remainingSeriesData = remainingSeriesData.map((value) => Math.max(0, value));
      styledSeries[node.id] = [
        styleEmissionSeries({
          data: this._valuesToSeriesData(years, remainingSeriesData),
          label: STATIC_DASHBOARD_IDS.REMAINING_EMISSIONS_ID,
          id: STATIC_DASHBOARD_IDS.REMAINING_EMISSIONS_ID,
        }),
        styleOtherReductionSeries({
          data: this._valuesToSeriesData(years, otherAreasData),
          label: STATIC_DASHBOARD_IDS.OTHER_REDUCTION_ID,
          id: STATIC_DASHBOARD_IDS.OTHER_REDUCTION_ID,
        }),
        styleAbatementSeries(
          {
            data: this._valuesToSeriesData(years, abatementData),
            label: node.title,
            id: node.id,
          },
          node
        ),
      ];
    });

    return styledSeries;
  }

  createTransitionScenarioDiagramData(
    boardId: string,
    nodes: Dict<DashboardNodeV4>,
    mainTransitionScenario: { [year: number]: MainTransitionScenarioItem },
    transitionScenarioData: Dict<{ [year: number]: TransitionScenarioItem }>
  ): TransitionScenarioDiagramData {
    const styledSeries: Dict<StyledSeries[]> = {};
    const styleEmissionSeries = (series: Series) => {
      return this._styleAsAreaSeries(series, Colours.primaryDashboard500, Colours.primaryDashboard300);
    };
    const styleGapSeries = (series: Series) => {
      return this._styleAsAreaSeries(series, Colours.primaryDashboard200, Colours.primaryDashboard100);
    };
    const styleEnergySeries = (series: Series) => {
      return this._styleAsAreaSeries(series, Colours.energy400, Colours.energy300);
    };
    const styleAbatementSeries = (series: Series, node: DashboardNodeV4) => {
      return this._styleAsAreaSeries(series, node.colours.main ?? '#aaaaaa', node.colours.light ?? '#ffffff');
    };
    // main series (board)
    const years = Object.keys(mainTransitionScenario);
    const endYear = years[years.length - 1];
    const endYearGoal = mainTransitionScenario[endYear].goalEmissions;
    let mainEmissionData: number[] = [];
    const mainGapData: number[] = [];
    const mainAbatementData: Dict<number[]> = {};
    years.forEach((year) => {
      const item: MainTransitionScenarioItem = mainTransitionScenario[year];
      mainEmissionData.push(this._roundValue(item.remainingEmissions));
      mainGapData.push(this._roundValue(item.gap));
      Object.keys(item.abatement).forEach((key) => {
        const abatement = item.abatement[key];
        const abatementId = abatement.id;
        if (!mainAbatementData[abatementId]) {
          mainAbatementData[abatementId] = [];
        }
        mainAbatementData[abatementId].push(this._roundValue(abatement.value));
      });
    });
    let allAbatementData = Object.keys(mainAbatementData).map((abatementId) => {
      const abatementData = mainAbatementData[abatementId];
      return abatementData;
    });
    Object.keys(mainAbatementData).forEach((abatementId, index) => {
      mainAbatementData[abatementId] = allAbatementData[index];
    });

    const mainEmissions: StyledSeries = styleEmissionSeries({
      data: this._valuesToSeriesData(years, mainEmissionData),
      label: STATIC_DASHBOARD_IDS.REMAINING_EMISSIONS_ID,
      id: STATIC_DASHBOARD_IDS.REMAINING_EMISSIONS_ID,
    });
    let mainSeries = [mainEmissions];
    const hasValidGapSeries = mainGapData.some((v) => v > 0);
    if (hasValidGapSeries) {
      const validGapData = mainGapData.map((value) => (value === undefined ? value : Math.max(0, value)));
      const mainGap: StyledSeries = styleGapSeries({
        data: this._valuesToSeriesData(years, validGapData),
        label: STATIC_DASHBOARD_IDS.MAIN_GAP_ID,
        id: STATIC_DASHBOARD_IDS.MAIN_GAP_ID,
      });
      mainSeries.push(mainGap);
    }

    const abatementSeries = Object.keys(mainAbatementData)
      .map((abatementId) => {
        const series: Series = {
          data: this._valuesToSeriesData(years, mainAbatementData[abatementId]),
          label: abatementId,
          id: abatementId,
        };
        if (abatementId === STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID) {
          return styleEnergySeries(series);
        }
        const node = nodes[abatementId];
        return styleAbatementSeries(series, node);
      })
      .reverse(); //reversing since we want the first node to be at the top of the area chart. Series in the area chart is added bottom up.
    mainSeries = mainSeries.concat(abatementSeries);
    styledSeries[boardId] = mainSeries;

    // node series

    Object.values(nodes).forEach((node) => {
      const scenarioData = transitionScenarioData[node.id];
      const years = Object.keys(scenarioData);
      let abatementData: Dict<number[]> = {};
      years.forEach((year) => {
        const item: TransitionScenarioItem = scenarioData[year];
        item.abatement.forEach((abatementEntry) => {
          const abatementId = abatementEntry.id;
          const abatementValue = abatementEntry.value;
          if (!abatementData[abatementId]) {
            abatementData[abatementId] = [];
          }
          abatementData[abatementId].push(this._roundValue(abatementValue));
        });
      });

      styledSeries[node.id] = Object.entries(abatementData).map(([abatementId, abatementData]) => {
        const childNode = nodes[abatementId] ?? { title: abatementId, id: abatementId };
        return styleAbatementSeries(
          {
            data: this._valuesToSeriesData(years, abatementData),
            label: childNode.title,
            id: childNode.id,
          },
          node
        );
      });
    });

    return {
      series: styledSeries,
      endYearGoal,
    };
  }

  createTransitionTargetDataSeries(
    nodes: Dict<DashboardNodeV4>,
    transitionData: Dict<{ [year: number]: TransitionItem }>,
    isV42: boolean
  ): Dict<StyledSeries[]> {
    const styledSeries: Dict<StyledSeries[]> = {};
    Object.values(nodes).forEach((node) => {
      const data = transitionData[node.id];

      if (data) {
        const targetData: SeriesData[] = [];
        const outcomeData: SeriesData[] = [];

        Object.keys(data).forEach((year) => {
          const entry: TransitionItem = data[year];
          targetData.push({ label: year, value: entry.target });
          outcomeData.push({ label: year, value: entry.outcome });
        });
        //Todo: exclude empty series? e.g series with only undefined values.
        const targetSeries: Series = {
          data: targetData,
          label: STATIC_DASHBOARD_IDS.TRANSITION_ID,
          id: STATIC_DASHBOARD_IDS.TRANSITION_ID,
        };
        const outcomeSeries: Series = {
          data: outcomeData,
          label: STATIC_DASHBOARD_IDS.OUTCOME_ID,
          id: STATIC_DASHBOARD_IDS.OUTCOME_ID,
        };
        styledSeries[node.id] = [
          this._styleAsTargetSeries(targetSeries, node.colours, isV42),
          this._styleAsOutcomeLineSeries(
            outcomeSeries,
            findLastIndex(outcomeSeries.data, (seriesData) => seriesData.value !== undefined)
          ),
        ];
      }
    });

    return styledSeries;
  }

  private _roundValue(value: number | undefined): number | undefined {
    return value === undefined ? undefined : Math.round(value);
  }

  private _styleAsAreaSeries(series: Series, colour: string, highlightColour: string): StyledSeries {
    const styled: StyledSeries = {
      ...series,
      style: {
        type: SeriesType.Area,
        fillColour: RGBAColour.fromHex(colour),

        separator: {
          colour: RGBAColour.fromHex(Colours.primaryDashboard700),
          width: 2,
        },
      },
      highlightStyle: {
        type: SeriesType.Area,
        fillColour: RGBAColour.fromHex(highlightColour),
        separator: {
          colour: RGBAColour.fromHex(Colours.primaryDashboard700),
          width: 2,
        },
      },
    };
    return styled;
  }

  private _styleAsOutcomeLineSeries(series: Series, focusedIndex?: number): StyledSeries {
    const colour = '#FFFFFF';

    const lineStyle: LineSeriesStyle = {
      dotStyle: {
        colour,
        diameter: 9,
      },
      focusedDotStyle: {
        colour,
        diameter: 9,
      },
      strokeStyle: {
        colour,
        width: 3,
      },
      type: SeriesType.Line,
      focusedIndex,
    };
    return { ...series, style: lineStyle };
  }

  private _styleAsTargetSeries(series: Series, nodeColours: DashboardColours, isV42: boolean): StyledSeries {
    let colour = isV42 ? nodeColours.main : nodeColours.light;
    let style: LineSeriesStyle;
    style = {
      type: SeriesType.Line,
      strokeStyle: {
        colour,
        width: 3,
        strokeType: {
          dash: {
            dash: 4,
            gap: 4,
          },
        },
      },
      focusedDotStyle: {
        colour: nodeColours.main,
        outline: {
          colour,
          width: 1,
        },
        diameter: 9,
      },
      focusedIndex: series.data?.length - 1 || 0,
    };

    return { ...series, style };
  }

  private _valuesToSeriesData(years: string[], values: number[]): SeriesData[] {
    return years.map((year, index) => {
      return { label: year, value: values[index] };
    });
  }
}
